import React, { ReactNode } from 'react';
import { RecordContextProvider, RecordType } from './RecordContext';
import { useFetch } from 'hooks/useFetchData';
import { useResourceContext } from './ResourceContext';
import { useParams } from 'react-router';
import { Error, Loader } from 'components/common/General';

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'Failed to load data.';
};

const isPermissionDenied = (message: string) => /permission deny/i.test(message);

const ResourceRecordContextProvider = <T extends RecordType>(props: ResourceRecordContextProviderProps) => {
  const { resource } = useResourceContext();
  const params = useParams();
  const { param, id = params[param!], ...rest } = props;
  const { data, loading, error, refetch } = useFetch<{ item: T }>('GET_ONE', resource, { variables: { id } });

  if (loading) {
    return <Loader />;
  }
  if (error) {
    const message = getErrorMessage(error);
    return (
      <Error
        message={isPermissionDenied(message) ? 'You do not have permission to view this resource.' : message}
      />
    );
  }

  return <RecordContextProvider refresh={refetch} {...rest} value={data!.item as T} id={(data!.item as T)[param!] || id} />;
};

export default ResourceRecordContextProvider;

export interface ResourceRecordContextProviderProps {
  param?: string;
  id?: string;
  children: ReactNode;
}
